import { useEffect, useState } from 'react';
import { Platform, Pressable, SafeAreaView, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { type Href, Link, router, useLocalSearchParams } from 'expo-router';
import { Image } from 'expo-image';
import Svg, { Path } from 'react-native-svg';

import { AuthButton, AuthInput, AuthLinkRow, AuthNote, AuthScreen } from '@/components/auth/auth-ui';
import { Fonts } from '@/constants/theme';
import { canAccessRoleAccount, getRoleHomePath, sanitizeNextPath } from '@/lib/auth-routing';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';
import slideOneSource from '../../assets/auth-slider/slide-1.webp';
import slideThreeSource from '../../assets/auth-slider/slide-3.webp';
import slideTwoSource from '../../assets/auth-slider/slide-2.webp';
import logoSource from '../../assets/images/mcqgen-logo-trimmed.png';

const desktopSlides = [
  slideOneSource,
  slideTwoSource,
  slideThreeSource,
];

export default function SignInScreen() {
  const { isConfigured } = useAuth();
  const params = useLocalSearchParams<{ next?: string }>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [desktopSlide, setDesktopSlide] = useState(0);
  const { width } = useWindowDimensions();
  const isTablet = width >= 768;
  const isDesktop = width >= 1024;
  const useSplitLayout = isTablet;

  useEffect(() => {
    if (!useSplitLayout) {
      return;
    }

    const timer = setInterval(() => {
      setDesktopSlide((current) => (current + 1) % desktopSlides.length);
    }, 3200);

    return () => clearInterval(timer);
  }, [useSplitLayout]);

  const handleSignIn = async () => {
    if (!email || !password) {
      setAuthError('Enter both email and password.');
      return;
    }

    if (!isConfigured) {
      setAuthError('Supabase is not configured yet. Add the project URL and publishable key first.');
      return;
    }

    setAuthError(null);
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    setLoading(false);

    if (error) {
      setAuthError(error.message || 'Unable to sign in with these credentials.');
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role, is_active')
      .eq('id', data.user.id)
      .single();

    if (profileError || !profile) {
      await supabase.auth.signOut({ scope: 'local' });
      setAuthError('We could not load this account right now. Please try again or contact support.');
      return;
    }

    if (!canAccessRoleAccount(profile.role, profile.is_active)) {
      await supabase.auth.signOut({ scope: 'local' });
      setAuthError('This account has been marked inactive. Please contact your school administrator.');
      return;
    }

    router.replace(
      (sanitizeNextPath(typeof params.next === 'string' ? params.next : null, profile.role) ??
        getRoleHomePath(profile.role)) as Href
    );
  };

  const handleGoogleSignIn = async () => {
    if (!isConfigured) {
      setAuthError('Supabase is not configured yet. Add the project URL and publishable key first.');
      return;
    }

    if (Platform.OS !== 'web') {
      setAuthError('Google sign-in is currently enabled only for the desktop web sign-in flow.');
      return;
    }

    const redirectTo =
      typeof window !== 'undefined'
        ? (() => {
            const callbackUrl = new URL('/auth/callback', window.location.origin);
            const safeNext =
              typeof params.next === 'string' ? sanitizeNextPath(params.next) : null;

            if (safeNext) {
              callbackUrl.searchParams.set('next', safeNext);
            }

            return callbackUrl.toString();
          })()
        : undefined;

    setAuthError(null);
    setGoogleLoading(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
      },
    });

    if (error) {
      setGoogleLoading(false);
      setAuthError(error.message || 'Google sign in failed.');
    }
  };

  if (!useSplitLayout) {
    return (
      <AuthScreen
        eyebrow="Welcome back"
        title="Sign in to MCQGen."
        subtitle="Use the school account tied to your workspace to continue.">
        <AuthInput
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          label="Email"
          onChangeText={(value) => {
            setEmail(value);
            if (authError) setAuthError(null);
          }}
          placeholder="admin@school.com"
          value={email}
        />
        <AuthInput
          autoCapitalize="none"
          autoComplete="password"
          label="Password"
          onChangeText={(value) => {
            setPassword(value);
            if (authError) setAuthError(null);
          }}
          placeholder="Enter your password"
          secureTextEntry
          value={password}
        />

        {authError ? <AuthNote tone="warn">{authError}</AuthNote> : null}

        <Pressable onPress={() => router.push('/forgot-password')} style={styles.inlineLink}>
          <Text style={styles.inlineLinkText}>Forgot password?</Text>
        </Pressable>

        <AuthButton loading={loading} onPress={handleSignIn} title="Sign in" />

        <AuthNote tone="neutral">
          You will land in the correct teacher, student, or admin workspace after sign in.
        </AuthNote>

        <AuthLinkRow actionHref="/sign-up" actionLabel="Create account" prompt="New here?" />

        <Link href="/" style={styles.secondaryLink}>
          Back to welcome
        </Link>
      </AuthScreen>
    );
  }

  return (
    <SafeAreaView style={styles.desktopSafeArea}>
      <View style={styles.desktopBg}>
        <View style={styles.desktopGlowOrbOne} />
        <View style={styles.desktopGlowOrbTwo} />
        <ScrollView
          contentContainerStyle={[styles.desktopScroll, !isDesktop && styles.desktopScrollTablet]}
          showsVerticalScrollIndicator={false}>
          <View style={[styles.desktopShell, !isDesktop && styles.desktopShellTablet]}>
            <View style={[styles.desktopFrame, !isDesktop && styles.desktopFrameTablet]}>
              <View
                style={[
                  styles.desktopFormPanel,
                  !isDesktop && styles.desktopFormPanelTablet,
                ]}>
                <View style={styles.desktopCard}>
                  <View style={styles.desktopCardHeader}>
                    <View style={styles.desktopBrandRow}>
                      <View style={styles.desktopBrandMark}>
                        <Image contentFit="contain" source={logoSource} style={styles.desktopLogo} />
                      </View>
                      <View style={styles.desktopBrandCopy}>
                        <Text style={styles.desktopBrandKicker}>MCQGen</Text>
                        <Text style={styles.desktopBrandSub}>Assessment operations</Text>
                      </View>
                    </View>

                    <View style={styles.desktopHeadlineBlock}>
                      <Text style={styles.desktopEyebrow}>Welcome back</Text>
                      <Text style={styles.desktopTitle}>Sign in.</Text>
                      <Text style={styles.desktopSubtitle}>Use your school account.</Text>
                    </View>
                  </View>

                  <Pressable
                    disabled={loading || googleLoading}
                    onPress={handleGoogleSignIn}
                    style={({ pressed }) => [
                      styles.desktopGoogleButton,
                      (loading || googleLoading) && styles.desktopGoogleButtonDisabled,
                      pressed && !(loading || googleLoading) && styles.desktopGoogleButtonPressed,
                    ]}>
                    <View style={styles.desktopGoogleIcon}>
                      <GoogleIcon />
                    </View>
                    <Text style={styles.desktopGoogleButtonText}>
                      {googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
                    </Text>
                  </Pressable>

                  <View style={styles.desktopDividerRow}>
                    <View style={styles.desktopDividerLine} />
                    <Text style={styles.desktopDividerText}>or use email</Text>
                    <View style={styles.desktopDividerLine} />
                  </View>

                  <AuthInput
                    autoCapitalize="none"
                    autoComplete="email"
                    keyboardType="email-address"
                    label="Email"
                    onChangeText={(value) => {
                      setEmail(value);
                      if (authError) setAuthError(null);
                    }}
                    placeholder="admin@school.com"
                    value={email}
                  />
                  <AuthInput
                    autoCapitalize="none"
                    autoComplete="password"
                    label="Password"
                    onChangeText={(value) => {
                      setPassword(value);
                      if (authError) setAuthError(null);
                    }}
                    placeholder="Enter your password"
                    secureTextEntry
                    value={password}
                  />

                  {authError ? <AuthNote tone="warn">{authError}</AuthNote> : null}

                  <View style={styles.desktopHelpRow}>
                    <Text style={styles.desktopHelpText}>Keep me signed in</Text>
                    <Pressable onPress={() => router.push('/forgot-password')} style={styles.desktopResetLink}>
                      <Text style={styles.inlineLinkText}>Reset password</Text>
                    </Pressable>
                  </View>

                  <AuthButton loading={loading} onPress={handleSignIn} title="Sign in" />

                  <AuthNote tone="neutral">
                    Access is role-aware: admin, teacher, and student dashboards stay separated.
                  </AuthNote>

                  <View style={styles.desktopFooterRow}>
                    <Text style={styles.desktopFooterPrompt}>New here?</Text>
                    <Link href="/sign-up" style={styles.desktopFooterAction}>
                      Create account
                    </Link>
                  </View>

                  <Link href="/" style={styles.desktopFooterBack}>
                    Back to welcome
                  </Link>
                </View>
              </View>

              <View
                style={[
                  styles.desktopBrandPanel,
                  !isDesktop && styles.desktopBrandPanelTablet,
                ]}>
                <View style={styles.desktopTestimonialCard}>
                  <View style={styles.desktopSliderStage}>
                    <View style={styles.desktopSliderImageWrap}>
                      <Image
                        contentFit="cover"
                        source={desktopSlides[desktopSlide]}
                        style={styles.desktopSliderImage}
                      />
                    </View>
                    <View style={styles.desktopSliderDots}>
                      {desktopSlides.map((_, index) => (
                        <Pressable
                          key={index}
                          onPress={() => setDesktopSlide(index)}
                          style={[
                            styles.desktopSliderDot,
                            index === desktopSlide && styles.desktopSliderDotActive,
                          ]}
                        />
                      ))}
                    </View>
                  </View>
                </View>
                <View style={styles.desktopTestimonialNotch} />
                <Text style={styles.desktopTestimonialTitle}>What school teams rely on most.</Text>
                <Text style={styles.desktopQuoteMark}>“</Text>
                <Text style={styles.desktopQuoteText}>One place for sets, scans, and results.</Text>
                <View style={styles.desktopQuoteAuthorBlock}>
                  <Text style={styles.desktopQuoteAuthor}>Ritika Sharma</Text>
                  <Text style={styles.desktopQuoteRole}>Assessment coordinator</Text>
                </View>

                <View style={styles.desktopMetricRow}>
                  <View style={styles.desktopInfoActionWarm}>
                    <Text style={styles.desktopInfoActionText}>62 sets this week</Text>
                  </View>
                  <View style={styles.desktopInfoActionCool}>
                    <Text style={styles.desktopInfoActionText}>186 scans cleared</Text>
                  </View>
                </View>

                <View style={styles.desktopPreviewPanel}>
                  <Text style={styles.desktopPreviewKicker}>Today</Text>
                  <Text style={styles.desktopPreviewTitle}>Exam readiness</Text>
                  <Text style={styles.desktopPreviewBody}>Review is on track.</Text>

                  <View style={styles.desktopMetricGrid}>
                    <View style={styles.desktopMetricCard}>
                      <Text style={styles.desktopMetricValue}>42</Text>
                      <Text style={styles.desktopMetricLabel}>Sets generated</Text>
                    </View>
                    <View style={styles.desktopMetricCard}>
                      <Text style={styles.desktopMetricValue}>96%</Text>
                      <Text style={styles.desktopMetricLabel}>Scan accuracy</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
}

function GoogleIcon() {
  return (
    <Svg width={20} height={20} viewBox="0 0 48 48">
      <Path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.6 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <Path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.4 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z"
      />
      <Path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.5-5.3l-6.2-5.1C29.2 35.2 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.5 16.2 44 24 44z"
      />
      <Path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1 2.8-3 5.1-5.9 6.6l6.2 5.1C39.2 36.4 44 30.8 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </Svg>
  );
}

const styles = StyleSheet.create({
  inlineLink: {
    alignSelf: 'flex-end',
    marginTop: -6,
  },
  inlineLinkText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    textDecorationLine: 'underline',
  },
  secondaryLink: {
    alignSelf: 'center',
    color: '#6D8071',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopSafeArea: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopBg: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopGlowOrbOne: {
    position: 'absolute',
    width: 460,
    height: 460,
    borderRadius: 230,
    top: -150,
    left: -130,
    backgroundColor: 'rgba(202, 221, 204, 0.9)',
  },
  desktopGlowOrbTwo: {
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: 260,
    right: -170,
    bottom: -170,
    backgroundColor: 'rgba(229, 217, 193, 0.78)',
  },
  desktopScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingVertical: 24,
  },
  desktopScrollTablet: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  desktopShell: {
    maxWidth: 1240,
    width: '100%',
    alignSelf: 'center',
  },
  desktopShellTablet: {
    maxWidth: 760,
  },
  desktopFrame: {
    width: '100%',
    minHeight: 640,
    flexDirection: 'row-reverse',
    borderRadius: 34,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    shadowColor: '#1E2C22',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.16,
    shadowRadius: 38,
    elevation: 10,
  },
  desktopFrameTablet: {
    minHeight: 0,
    flexDirection: 'column-reverse',
  },
  desktopFormPanel: {
    flex: 0.92,
    backgroundColor: '#F6F1E8',
    paddingHorizontal: 34,
    paddingVertical: 34,
    borderLeftWidth: 1,
    borderLeftColor: '#D9E3DA',
  },
  desktopFormPanelTablet: {
    flex: 0,
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderTopColor: '#D9E3DA',
    paddingHorizontal: 24,
    paddingVertical: 28,
  },
  desktopBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  desktopBrandMark: {
    width: 68,
    height: 68,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopLogo: {
    width: 52,
    height: 52,
  },
  desktopBrandCopy: {
    gap: 3,
  },
  desktopBrandKicker: {
    color: '#173A2A',
    fontSize: 22,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopBrandSub: {
    color: '#6B7D71',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopHeadlineBlock: {
    gap: 4,
  },
  desktopEyebrow: {
    color: '#7A8C63',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  desktopTitle: {
    color: '#173A2A',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopSubtitle: {
    color: '#5F7166',
    fontSize: 14,
    lineHeight: 20,
  },
  desktopCard: {
    width: '100%',
    height: '100%',
    gap: 18,
    justifyContent: 'center',
  },
  desktopCardHeader: {
    gap: 16,
    marginBottom: 8,
  },
  desktopGoogleButton: {
    minHeight: 56,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#D5DED5',
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 16,
  },
  desktopGoogleButtonDisabled: {
    opacity: 0.55,
  },
  desktopGoogleButtonPressed: {
    transform: [{ scale: 0.99 }],
  },
  desktopGoogleIcon: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopGoogleButtonText: {
    color: '#173A2A',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  desktopDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#D8DED7',
  },
  desktopDividerText: {
    color: '#7A867D',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  desktopHelpRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginTop: -3,
  },
  desktopHelpText: {
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopResetLink: {
    alignSelf: 'center',
  },
  desktopFooterRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  desktopFooterPrompt: {
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopFooterAction: {
    color: '#173A2A',
    fontSize: 13,
    fontWeight: '800',
    textDecorationLine: 'underline',
  },
  desktopFooterBack: {
    alignSelf: 'flex-start',
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 'auto',
  },
  desktopBrandPanel: {
    flex: 1.08,
    backgroundColor: '#173A2A',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  desktopBrandPanelTablet: {
    flex: 0,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  desktopTestimonialCard: {
    flex: 1,
    borderRadius: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: 'center',
  },
  desktopSliderStage: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderImageWrap: {
    flex: 1,
    width: '100%',
    alignSelf: 'stretch',
    aspectRatio: 0.75,
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderWidth: 0,
    overflow: 'hidden',
  },
  desktopSliderImage: {
    width: '100%',
    height: '100%',
  },
  desktopSliderDots: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(247,243,235,0.36)',
  },
  desktopSliderDotActive: {
    width: 28,
    backgroundColor: '#F7F3EB',
  },
  desktopTestimonialNotch: {
    display: 'none',
  },
  desktopTestimonialTitle: {
    display: 'none',
  },
  desktopQuoteMark: {
    display: 'none',
  },
  desktopQuoteText: {
    display: 'none',
  },
  desktopQuoteAuthorBlock: {
    display: 'none',
  },
  desktopQuoteAuthor: {
    display: 'none',
  },
  desktopQuoteRole: {
    display: 'none',
  },
  desktopMetricRow: {
    display: 'none',
  },
  desktopInfoActionWarm: {
    display: 'none',
  },
  desktopInfoActionCool: {
    display: 'none',
  },
  desktopInfoActionText: {
    display: 'none',
  },
  desktopPreviewPanel: {
    display: 'none',
  },
  desktopPreviewKicker: {
    display: 'none',
  },
  desktopPreviewTitle: {
    display: 'none',
  },
  desktopPreviewBody: {
    display: 'none',
  },
  desktopMetricGrid: {
    display: 'none',
  },
  desktopMetricCard: {
    display: 'none',
  },
  desktopMetricValue: {
    display: 'none',
  },
  desktopMetricLabel: {
    display: 'none',
  },
});
